import * as Accordion from '@radix-ui/react-accordion';
import * as Select from '@radix-ui/react-select';
import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { Icon } from '~/components';
import { DropdownItemLabel } from '~/components/DropdownTreeSingleSelect/components/DropdownItemLabel';
import { DropdownItemTrigger } from '~/components/DropdownTreeSingleSelect/components/DropdownItemTrigger';
import type {
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/components/DropdownTreeSingleSelect/types';
import { cn } from '~/lib/cn';

interface DropdownItemProps extends RadixUiProps {
  depth: number;
  option: InternalDropdownOption;
  selectedOption?: InternalDropdownOption;
  onSelectOption?: (option: InternalDropdownOption) => void;
  showCheckIcon?: boolean;
  searchQuery?: string;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownItem({
  depth,
  option,
  selectedOption,
  onSelectOption,
  showCheckIcon,
  searchQuery,
  OptionTemplate,
  radix = {},
}: DropdownItemProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isUnselectableParent = option.unselectable && !!option.children?.length;

  // Toggle the accordion open/closed to reflect the isExpanded state.
  useEffect(() => {
    if (!option.isExpanded && triggerRef.current?.dataset.state === 'open') {
      triggerRef.current.click();
    }

    if (option.isExpanded && triggerRef.current?.dataset.state === 'closed') {
      triggerRef.current.click();
    }
  }, [option.isExpanded]);

  const handleOptionInteraction = useCallback(() => {
    if (isUnselectableParent) {
      triggerRef.current?.click();

      return;
    }

    onSelectOption?.(option);
  }, [isUnselectableParent, onSelectOption, option]);

  const handleOptionMouseUp = useCallback(() => {
    handleOptionInteraction();
  }, [handleOptionInteraction]);

  const handleOptionKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (['Enter', ' '].includes(event.key)) {
        handleOptionInteraction();
      }
    },
    [handleOptionInteraction],
  );

  let OptionLabel = OptionTemplate || DropdownItemLabel;

  const itemClassName = cn(
    'flex cursor-pointer gap-xs px-md py-sm transition-colors duration-300 text-default',
    'hover:bg-emphasis-strong focus:outline-none focus:bg-emphasis-strong',
    {
      'select-none': isUnselectableParent,
      'focus:bg-secondary-strong bg-primary-default hover:bg-secondary-default':
        !isUnselectableParent && selectedOption?.value === option.value,
    },
    radix.selectItem?.className,
  );

  const itemStyle = {
    paddingLeft: depth ? `calc(var(--spacing-md) * ${depth + 1})` : undefined,
    ...radix.selectItem?.style,
  };

  const itemContent = (
    <>
      {showCheckIcon ? (
        <div className="-ml-xs w-md">
          {!isUnselectableParent ? (
            <Select.ItemIndicator>
              <Icon type="check" size="sm" />
            </Select.ItemIndicator>
          ) : null}
        </div>
      ) : null}
      {isUnselectableParent ? (
        <OptionLabel option={option} searchQuery={searchQuery} />
      ) : (
        <Select.ItemText>
          <OptionLabel option={option} searchQuery={searchQuery} />
        </Select.ItemText>
      )}
    </>
  );

  return (
    <Accordion.Header
      {...radix.accordionHeader}
      className={cn('relative', radix.accordionHeader?.className)}
    >
      {option.children?.length ? (
        <div className="absolute inset-y-0 end-0 flex items-center">
          <DropdownItemTrigger triggerRef={triggerRef} option={option} />
        </div>
      ) : null}
      {isUnselectableParent ? (
        <div
          className={itemClassName}
          style={itemStyle}
          onMouseUp={handleOptionMouseUp}
          onKeyUp={handleOptionKeyUp}
          tabIndex={0}
        >
          {itemContent}
        </div>
      ) : (
        <Select.Item
          {...radix.selectItem}
          value={option.value}
          className={itemClassName}
          style={itemStyle}
          onMouseUp={handleOptionMouseUp}
          onKeyUp={handleOptionKeyUp}
          asChild
          aria-selected={selectedOption?.value === option.value}
        >
          <div>{itemContent}</div>
        </Select.Item>
      )}
    </Accordion.Header>
  );
}

DropdownItem.displayName = 'DropdownItem';
